import api from './api'
import type {
  ClubInvitationDetail,
  ClubInvitationLookupItem,
  ClubInvitationSendResponse,
  ClubInvitationsResponse,
  ClubInvitationStatus
} from '../types/clubInvitation.types'

/**
 * Club Invitation Service
 * Handles e-mail invitations to the club signup form
 */
class ClubInvitationService {
  /**
   * Get sent invitations
   * GET v1/{club}/club-invitations
   */
  async getInvitations(
    clubUuid: string,
    page: number = 1,
    perPage: number = 25,
    search: string = '',
    status: ClubInvitationStatus | '' = ''
  ): Promise<ClubInvitationsResponse> {
    const params: Record<string, any> = { page, per_page: perPage }
    if (search.trim()) params.search = search.trim()
    if (status) params.status = status
    const response = await api.get<ClubInvitationsResponse>(`v1/${clubUuid}/club-invitations`, { params })
    return response.data
  }

  /**
   * Get single invitation with its status history
   * GET v1/{club}/club-invitations/{uuid}
   */
  async getInvitation(clubUuid: string, invitationUuid: string): Promise<ClubInvitationDetail> {
    const response = await api.get<{ invitation: ClubInvitationDetail }>(
      `v1/${clubUuid}/club-invitations/${invitationUuid}`
    )
    return response.data.invitation
  }

  /**
   * Send signup link to e-mail addresses
   * POST v1/{club}/club-invitations
   */
  async send(clubUuid: string, emails: string[]): Promise<ClubInvitationSendResponse> {
    const response = await api.post<ClubInvitationSendResponse>(`v1/${clubUuid}/club-invitations`, { emails })
    return response.data
  }

  /**
   * Send invitations again
   * POST v1/{club}/club-invitations/resend
   */
  async resend(clubUuid: string, invitationUuids: string[]): Promise<ClubInvitationSendResponse> {
    const response = await api.post<ClubInvitationSendResponse>(`v1/${clubUuid}/club-invitations/resend`, {
      invitation_uuids: invitationUuids
    })
    return response.data
  }

  /**
   * Delete invitations
   * POST v1/{club}/club-invitations/delete
   */
  async remove(clubUuid: string, invitationUuids: string[]): Promise<{ message: string; deleted: number }> {
    const response = await api.post(`v1/${clubUuid}/club-invitations/delete`, {
      invitation_uuids: invitationUuids
    })
    return response.data
  }

  /**
   * Check which addresses were already invited or already belong to the club
   * POST v1/{club}/club-invitations/lookup
   */
  async lookup(clubUuid: string, emails: string[]): Promise<ClubInvitationLookupItem[]> {
    const response = await api.post<{ items: ClubInvitationLookupItem[] }>(
      `v1/${clubUuid}/club-invitations/lookup`,
      { emails }
    )
    return response.data.items || []
  }
}

export default new ClubInvitationService()
